import { useEffect, useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  FileSearch,
  FileText,
  BookOpen,
  ShieldCheck,
  Palette,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";

import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { WordAddinWaitlistDialog } from "@/components/word-addin-waitlist-dialog";
import { Faqs } from "@/components/pricing/faqs";
import { WORD_ADDIN_PAGE, WORD_ADDIN_PRICING } from "@/lib/content";

const tabIcons = [FileSearch, FileText, BookOpen];

export default function WordAddinPage() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  // `tier` is the stable identifier ("Free"/"Pro") sent to the backend and
  // analytics; `tierLabel` is the friendly name ("Solo Counsel"/"Team Pro")
  // shown on cards and in the dialog title. Looking the label up from the
  // plans list keeps the two surfaces in sync.
  const heroPlan = WORD_ADDIN_PRICING.plans.find(
    (p) => p.name === WORD_ADDIN_PAGE.hero.primaryCta.tier,
  );
  const [selectedTier, setSelectedTier] = useState<string>(
    WORD_ADDIN_PAGE.hero.primaryCta.tier,
  );
  const [selectedTierLabel, setSelectedTierLabel] = useState<string>(
    heroPlan?.displayName ?? WORD_ADDIN_PAGE.hero.primaryCta.tier,
  );

  // Same hash-aware scroll behavior as the pricing page so deep links like
  // /word-addin#waitlist or /word-addin#install land in the right section.
  useEffect(() => {
    function scrollToHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const timer = setTimeout(scrollToHash, 100);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  function openWaitlist(tier: string, tierLabel?: string) {
    setSelectedTier(tier);
    setSelectedTierLabel(
      tierLabel ??
        WORD_ADDIN_PRICING.plans.find((p) => p.name === tier)?.displayName ??
        tier,
    );
    setWaitlistOpen(true);
  }

  return (
    <Layout>
      <SEOHead
        title={WORD_ADDIN_PAGE.seo.title}
        description={WORD_ADDIN_PAGE.seo.description}
      />

      <WordAddinWaitlistDialog
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        selectedTier={selectedTier}
        selectedTierLabel={selectedTierLabel}
        source="word-addin-page"
      />

      {/* Hero — navy with task-pane mockup */}
      <section className="relative bg-navy text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none opacity-100" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80 mb-6 tracking-wide uppercase">
                {WORD_ADDIN_PAGE.hero.eyebrow}
              </div>
              <h1 className="text-display-xl mb-6 text-white">
                {WORD_ADDIN_PAGE.hero.headline}
              </h1>
              <p className="text-body-lg text-slate-300 mb-8 max-w-2xl">
                {WORD_ADDIN_PAGE.hero.subhead}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
                <Button
                  size="lg"
                  onClick={() => openWaitlist(WORD_ADDIN_PAGE.hero.primaryCta.tier)}
                  className="w-full sm:w-auto h-12 px-8 bg-brand-gradient text-white border-0 hover:opacity-90 transition-opacity text-base font-semibold no-default-hover-elevate"
                  data-testid="word-addin-hero-cta"
                >
                  {WORD_ADDIN_PAGE.hero.primaryCta.label}
                </Button>
                <Link href={WORD_ADDIN_PAGE.hero.secondaryCta.path}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto h-12 px-8 text-base font-semibold border-white/30 text-white bg-white/5 hover:bg-white/10 no-default-hover-elevate"
                  >
                    {WORD_ADDIN_PAGE.hero.secondaryCta.label}
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-slate-400">
                {WORD_ADDIN_PAGE.hero.note}
              </p>
            </motion.div>

            {/* Real screenshot of the ClauseArk task pane (light + dark variants) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="mx-auto max-w-sm rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2 border-b border-white/10 bg-white/5">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                    Word · Task pane
                  </span>
                </div>
                <picture>
                  <source
                    srcSet={`${import.meta.env.BASE_URL}word-addin/task-pane-dark.jpg`}
                    media="(prefers-color-scheme: dark)"
                  />
                  <img
                    src={`${import.meta.env.BASE_URL}word-addin/task-pane-light.jpg`}
                    alt="ClauseArk task pane in Microsoft Word showing an Acme MSA renewal with an overall risk summary, a Critical limitation-of-liability finding, and an expanded High-risk indemnity finding with a suggested redline ready to apply as a tracked change."
                    width={768}
                    height={830}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto"
                  />
                </picture>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-24 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-display text-navy mb-5">
              {WORD_ADDIN_PAGE.whoItsFor.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {WORD_ADDIN_PAGE.whoItsFor.body}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {WORD_ADDIN_PAGE.whoItsFor.audiences.map((aud, i) => (
              <motion.div
                key={aud.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-white rounded-2xl p-7 border border-mist shadow-sm"
              >
                <h3 className="text-h4 text-navy mb-3">{aud.title}</h3>
                <p className="text-body text-slate-600 leading-relaxed">
                  {aud.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs breakdown — Review / Summary / Clauses */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {WORD_ADDIN_PAGE.tabs.eyebrow}
            </div>
            <h2 className="text-display text-navy mb-5">
              {WORD_ADDIN_PAGE.tabs.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {WORD_ADDIN_PAGE.tabs.sub}
            </p>
          </div>
          <div className="space-y-12 max-w-5xl mx-auto">
            {WORD_ADDIN_PAGE.tabs.items.map((tab, i) => {
              const Icon = tabIcons[i] ?? Sparkles;
              return (
                <motion.div
                  key={tab.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 bg-white rounded-3xl border border-mist shadow-sm p-8 md:p-10"
                >
                  <div className="md:col-span-5">
                    <div className="h-12 w-12 rounded-xl bg-cyan-50 text-cyan flex items-center justify-center mb-5">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-2">
                      Tab {i + 1}
                    </div>
                    <h3 className="text-h3 text-navy mb-4">{tab.title}</h3>
                    <p className="text-body text-slate-600 leading-relaxed">
                      {tab.body}
                    </p>
                  </div>
                  <div className="md:col-span-7">
                    <ul className="space-y-3">
                      {tab.bullets.map((bullet) => (
                        <li
                          key={bullet}
                          className="flex items-start gap-3 text-sm text-slate-700"
                        >
                          <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it's different from generic AI in Word */}
      <section className="py-24 md:py-32 bg-navy text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan font-semibold mb-3">
              {WORD_ADDIN_PAGE.difference.eyebrow}
            </div>
            <h2 className="text-display text-white mb-5">
              {WORD_ADDIN_PAGE.difference.headline}
            </h2>
            <p className="text-body-lg text-slate-300">
              {WORD_ADDIN_PAGE.difference.body}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {WORD_ADDIN_PAGE.difference.rows.map((row, i) => (
              <motion.div
                key={row.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <h3 className="text-base font-semibold text-white mb-2">
                  {row.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {row.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Install / getting started */}
      <section id="install" className="py-24 md:py-32 bg-slate-50 scroll-mt-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {WORD_ADDIN_PAGE.install.eyebrow}
            </div>
            <h2 className="text-display text-navy mb-5">
              {WORD_ADDIN_PAGE.install.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {WORD_ADDIN_PAGE.install.sub}
            </p>
          </div>
          <div className="max-w-3xl mx-auto relative">
            <div
              aria-hidden
              className="absolute left-5 top-2 bottom-2 w-px bg-gradient-to-b from-indigo via-cyan to-transparent hidden md:block"
            />
            <ol className="space-y-10 md:space-y-14">
              {WORD_ADDIN_PAGE.install.steps.map((step, i) => (
                <motion.li
                  key={step.n}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="relative flex gap-5 md:gap-8"
                >
                  <div className="relative z-10 shrink-0 h-10 w-10 rounded-full bg-brand-gradient text-white font-bold text-sm flex items-center justify-center shadow-md">
                    {i + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <h3 className="text-h4 text-navy mb-2">{step.title}</h3>
                    <p className="text-body text-slate-600 leading-relaxed max-w-2xl">
                      {step.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>

            <div className="mt-12 md:mt-16 md:ml-[60px]">
              <div className="rounded-2xl border border-mist bg-white shadow-sm p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-1.5">
                    Need the deep version?
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {WORD_ADDIN_PAGE.install.fullGuide.hint}
                  </p>
                </div>
                <Link
                  href={WORD_ADDIN_PAGE.install.fullGuide.path}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:underline shrink-0"
                  data-testid="word-addin-full-install-guide-link"
                >
                  {WORD_ADDIN_PAGE.install.fullGuide.label} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & auth + theme/compatibility */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45 }}
              className="bg-white rounded-2xl p-8 border border-mist shadow-sm"
            >
              <div className="h-11 w-11 rounded-xl bg-indigo/10 text-indigo flex items-center justify-center mb-5">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-2">
                {WORD_ADDIN_PAGE.security.eyebrow}
              </div>
              <h3 className="text-h3 text-navy mb-3">
                {WORD_ADDIN_PAGE.security.headline}
              </h3>
              <p className="text-body text-slate-600 leading-relaxed mb-5">
                {WORD_ADDIN_PAGE.security.body}
              </p>
              <ul className="space-y-3">
                {WORD_ADDIN_PAGE.security.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="bg-white rounded-2xl p-8 border border-mist shadow-sm"
            >
              <div className="h-11 w-11 rounded-xl bg-cyan-50 text-cyan flex items-center justify-center mb-5">
                <Palette className="h-5 w-5" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-2">
                {WORD_ADDIN_PAGE.compatibility.eyebrow}
              </div>
              <h3 className="text-h3 text-navy mb-3">
                {WORD_ADDIN_PAGE.compatibility.headline}
              </h3>
              <p className="text-body text-slate-600 leading-relaxed mb-5">
                {WORD_ADDIN_PAGE.compatibility.body}
              </p>
              <ul className="space-y-3">
                {WORD_ADDIN_PAGE.compatibility.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing summary — pricing page is source of truth */}
      <section
        id="waitlist"
        className="py-24 md:py-28 bg-slate-50 border-y border-border/50 scroll-mt-20"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {WORD_ADDIN_PAGE.pricing.eyebrow}
            </div>
            <h2 className="text-display text-navy mb-5">
              {WORD_ADDIN_PAGE.pricing.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {WORD_ADDIN_PAGE.pricing.sub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {WORD_ADDIN_PRICING.plans.map((plan) => (
              <div
                key={plan.name}
                className={`flex flex-col rounded-3xl p-7 bg-white border ${
                  plan.featured
                    ? "border-2 border-indigo-500 shadow-xl relative"
                    : "border-mist shadow-sm"
                }`}
              >
                {plan.featured && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-navy mb-1">{plan.displayName}</h3>
                <div className="mb-1">
                  <span className="text-4xl font-extrabold text-navy">{plan.price}</span>
                  <span className="text-sm text-muted-foreground ml-1">{plan.cadence}</span>
                </div>
                {plan.billing && (
                  <div className="text-xs text-muted-foreground mb-3">{plan.billing}</div>
                )}
                <p className="text-sm text-slate-600 mb-6 min-h-[40px]">
                  {plan.tagline}
                </p>
                <Button
                  onClick={() => openWaitlist(plan.name, plan.displayName)}
                  className={`w-full ${
                    plan.featured
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : ""
                  }`}
                  variant={plan.featured ? "default" : "outline"}
                  data-testid={`word-addin-plan-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta.label}
                </Button>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              href={WORD_ADDIN_PAGE.pricing.fullPricingLink.path}
              className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:underline"
            >
              {WORD_ADDIN_PAGE.pricing.fullPricingLink.label}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs reuse the pricing page's component */}
      <Faqs faqs={WORD_ADDIN_PAGE.faqs} />

      {/* Final CTA — indigo band */}
      <section className="py-24 bg-brand-gradient text-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-3xl">
          <h2 className="text-display text-white mb-5">
            Ready to put ClauseArk inside Word?
          </h2>
          <p className="text-body-lg text-white/90 mb-10">
            Join the waitlist and we'll let you know the moment your tenant is enabled. No credit card, no sales call.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => openWaitlist(WORD_ADDIN_PAGE.hero.primaryCta.tier)}
              className="w-full sm:w-auto h-12 px-8 bg-white text-indigo hover:bg-slate-100 transition-colors text-base font-semibold no-default-hover-elevate"
              data-testid="word-addin-final-cta"
            >
              {WORD_ADDIN_PAGE.hero.primaryCta.label}
            </Button>
            <Link href="/pricing#word-addin">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 px-8 border-white/40 bg-transparent text-white hover:bg-white/10 text-base font-semibold no-default-hover-elevate"
              >
                See pricing
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
